import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { Invoice } from '../types/Invoice';
export function InvoiceDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock data - in a real app, this would be fetched from an API
  const invoice: Invoice = {
    id: id || '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+60 12 345 6789',
    amount: 125000,
    currency: 'Rs.',
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    notes: 'Thank you for your business!',
    items: [{
      id: '1',
      description: 'Premium Plan Subscription',
      quantity: 1,
      unitPrice: 100000,
      total: 100000
    }, {
      id: '2',
      description: 'Additional User Licenses',
      quantity: 5,
      unitPrice: 5000,
      total: 25000
    }],
    subtotal: 125000,
    taxAmount: 0,
    discountAmount: 0,
    total: 125000
  };
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`} data-id="element-7030">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="space-y-6" data-id="element-7031">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-7032">
        <button onClick={() => navigate('/invoices')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-7033">
          <ArrowLeftIcon size={20} data-id="element-7034" />
          Back to Invoices
        </button>
        <div className="flex items-center gap-3" data-id="element-7035">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-7036">
            <PrinterIcon size={18} data-id="element-7037" />
            Print
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-7038">
            <DownloadIcon size={18} data-id="element-7039" />
            Download PDF
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-7040">
            <MailIcon size={18} data-id="element-7041" />
            Send Email
          </button>
          <button onClick={() => navigate(`/invoice-form?id=${invoice.id}`)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-7042">
            <EditIcon size={18} data-id="element-7043" />
            Edit
          </button>
        </div>
      </div>
      {/* Invoice Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-8" data-id="element-7044">
        {/* Invoice Header */}
        <div className="flex justify-between items-start mb-8" data-id="element-7045">
          <div data-id="element-7046">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-7047">INVOICE</h1>
            <p className="text-lg text-gray-600" data-id="element-7048">{invoice.invoiceNumber}</p>
          </div>
          <div className="text-right" data-id="element-7049">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aZxoSgpvWGaSLuSteDWmq1/Maybank_logo.svg" alt="Maybank" className="h-12 mb-4" data-id="element-7050" />
            <p className="text-sm text-gray-600" data-id="element-7051">Maybank</p>
            <p className="text-sm text-gray-600" data-id="element-7052">Colombo, Sri Lanka</p>
          </div>
        </div>
        {/* Status and Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-gray-200" data-id="element-7053">
          <div data-id="element-7054">
            <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-7055">Bill To:</h3>
            <p className="text-lg font-semibold text-gray-900" data-id="element-7056">
              {invoice.customerName}
            </p>
            <p className="text-sm text-gray-600" data-id="element-7057">{invoice.customerEmail}</p>
            {invoice.customerPhone && <p className="text-sm text-gray-600" data-id="element-7058">{invoice.customerPhone}</p>}
          </div>
          <div className="text-right" data-id="element-7059">
            <div className="mb-4" data-id="element-7060">{getStatusBadge(invoice.status)}</div>
            <div className="space-y-2" data-id="element-7061">
              <div className="flex justify-end gap-4" data-id="element-7062">
                <span className="text-sm text-gray-500" data-id="element-7063">Issue Date:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-7064">
                  {new Date(invoice.issueDate).toLocaleDateString('en-GB')}
                </span>
              </div>
              <div className="flex justify-end gap-4" data-id="element-7065">
                <span className="text-sm text-gray-500" data-id="element-7066">Due Date:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-7067">
                  {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                </span>
              </div>
              {invoice.paidDate && <div className="flex justify-end gap-4" data-id="element-7068">
                  <span className="text-sm text-gray-500" data-id="element-7069">Paid Date:</span>
                  <span className="text-sm font-medium text-green-600" data-id="element-7070">
                    {new Date(invoice.paidDate).toLocaleDateString('en-GB')}
                  </span>
                </div>}
            </div>
          </div>
        </div>
        {/* Invoice Items */}
        <div className="mb-8" data-id="element-7071">
          <table className="min-w-full" data-id="element-7072">
            <thead data-id="element-7073">
              <tr className="border-b border-gray-200" data-id="element-7074">
                <th className="py-3 text-left text-sm font-medium text-gray-500" data-id="element-7075">
                  Description
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-7076">
                  Quantity
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-7077">
                  Unit Price
                </th>
                <th className="py-3 text-right text-sm font-medium text-gray-500" data-id="element-7078">
                  Total
                </th>
              </tr>
            </thead>
            <tbody data-id="element-7079">
              {invoice.items.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-7080">
                  <td className="py-4 text-sm text-gray-900" data-id="element-7081">
                    {item.description}
                  </td>
                  <td className="py-4 text-sm text-gray-900 text-right" data-id="element-7082">
                    {item.quantity}
                  </td>
                  <td className="py-4 text-sm text-gray-900 text-right" data-id="element-7083">
                    MYR {item.unitPrice.toLocaleString('en-MY')}
                  </td>
                  <td className="py-4 text-sm font-medium text-gray-900 text-right" data-id="element-7084">
                    MYR {item.total.toLocaleString('en-MY')}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Totals */}
        <div className="flex justify-end mb-8" data-id="element-7085">
          <div className="w-full max-w-sm space-y-2" data-id="element-7086">
            <div className="flex justify-between py-2" data-id="element-7087">
              <span className="text-sm text-gray-600" data-id="element-7088">Subtotal:</span>
              <span className="text-sm font-medium text-gray-900" data-id="element-7089">
                MYR {invoice.subtotal.toLocaleString('en-MY')}
              </span>
            </div>
            {invoice.discountAmount && invoice.discountAmount > 0 && <div className="flex justify-between py-2" data-id="element-7090">
                <span className="text-sm text-gray-600" data-id="element-7091">Discount:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-7092">
                  - MYR {invoice.discountAmount.toLocaleString('en-MY')}
                </span>
              </div>}
            {invoice.taxAmount && invoice.taxAmount > 0 && <div className="flex justify-between py-2" data-id="element-7093">
                <span className="text-sm text-gray-600" data-id="element-7094">Tax:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-7095">
                  MYR {invoice.taxAmount.toLocaleString('en-MY')}
                </span>
              </div>}
            <div className="flex justify-between py-3 border-t-2 border-gray-200" data-id="element-7096">
              <span className="text-lg font-semibold text-gray-900" data-id="element-7097">
                Total:
              </span>
              <span className="text-lg font-bold text-gray-900" data-id="element-7098">
                MYR {invoice.total.toLocaleString('en-MY')}
              </span>
            </div>
          </div>
        </div>
        {/* Notes */}
        {invoice.notes && <div className="pt-6 border-t border-gray-200" data-id="element-7099">
            <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-7100">Notes:</h3>
            <p className="text-sm text-gray-600" data-id="element-7101">{invoice.notes}</p>
          </div>}
      </div>
      {/* Action Buttons */}
      {invoice.status === 'sent' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7102">
          <div className="flex items-center justify-between" data-id="element-7103">
            <div data-id="element-7104">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-7105">
                Mark as Paid
              </h3>
              <p className="text-sm text-gray-600" data-id="element-7106">
                Confirm that this invoice has been paid
              </p>
            </div>
            <button className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-7107">
              <CheckCircleIcon size={20} data-id="element-7108" />
              Mark as Paid
            </button>
          </div>
        </div>}
      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-7109">
          <div className="flex items-center justify-between" data-id="element-7110">
            <div data-id="element-7111">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-7112">
                Cancel Invoice
              </h3>
              <p className="text-sm text-gray-600" data-id="element-7113">
                This action cannot be undone
              </p>
            </div>
            <button className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-7114">
              <XCircleIcon size={20} data-id="element-7115" />
              Cancel Invoice
            </button>
          </div>
        </div>}
    </div>;
}